import type { directory, services, location } from '@cwos/api-client';
import type { FindResult } from '@cwos/ui-admin';

/**
 * Global search pure logic (doc 04 §3). Tenant isolation and RBAC are enforced
 * at the endpoints (RLS-scoped, permission-gated) — this module only normalizes
 * and matches what the app is already allowed to fetch, and projects CLIENTS AS
 * INITIALS ONLY (the `Client` view carries `initials`, never a full name).
 */

/** Normalize a raw query: trim, lowercase, collapse internal whitespace. */
export function normalizeQuery(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Does `text` contain `query` (both normalized, case-insensitive)? */
export function textMatches(text: string | null | undefined, query: string): boolean {
  if (!text) return false;
  const q = normalizeQuery(query);
  if (q.length === 0) return false;
  return normalizeQuery(text).includes(q);
}

/** Does a result match on title or subtitle? */
export function resultMatches(result: FindResult, query: string): boolean {
  return textMatches(result.title, query) || textMatches(result.subtitle, query);
}

/** Filter the search index by a raw query (minimum 2 characters). */
export function searchResults(index: readonly FindResult[], rawQuery: string): FindResult[] {
  const q = normalizeQuery(rawQuery);
  if (q.length < 2) return [];
  return index.filter((r) => resultMatches(r, q));
}

/** Staff member → result. `displayName` is staff (not PHI). */
export function workerResult(w: directory.Worker): FindResult {
  return {
    id: `worker:${w.id}`,
    kind: 'worker',
    title: w.displayName,
    subtitle: w.roleTitle ?? 'Worker',
    href: '/people',
    icon: 'people',
  };
}

/** Client → result. INITIALS ONLY — the full name never appears in search. */
export function clientResult(c: directory.Client): FindResult {
  return {
    id: `client:${c.id}`,
    kind: 'client',
    title: c.initials,
    subtitle: `Client · ${c.status}`,
    href: null, // no client detail route in Admin yet — shown but not clickable
    icon: 'people',
  };
}

/** Service type → result. */
export function serviceResult(s: services.ServiceType): FindResult {
  return {
    id: `service:${s.id}`,
    kind: 'service',
    title: s.name,
    subtitle: s.code ? `Service · ${s.code}` : 'Service',
    href: '/settings/services',
    icon: 'delivery',
  };
}

/** Geofence / location → result. */
export function locationResult(g: location.Geofence): FindResult {
  return {
    id: `location:${g.id}`,
    kind: 'location',
    title: g.name,
    subtitle: 'Location',
    href: '/settings/location',
    icon: 'mapPin',
  };
}
